import React, { forwardRef, useCallback } from "react";
import { cn } from "@/shared/utils/shadcn";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/Tooltip";

interface TouchableProps extends React.HTMLAttributes<HTMLDivElement> {
  onClick?: () => void;
  href?: string;
  children: React.ReactNode;
  disabled?: boolean;
  tooltip?: string;
}

const Touchable = forwardRef<
  HTMLDivElement | HTMLAnchorElement,
  TouchableProps
>(({ onClick, href, children, disabled, tooltip, className, ...rest }, ref) => {
  const commonClasses =
    "transition-shadow duration-200 ease-in-out hover:shadow-hover";

  // When disabledDescription is provided, keep cursor as pointer, otherwise use cursor-not-allowed
  const disabledClasses = disabled ? "cursor-not-allowed opacity-50" : "";

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick && onClick();
    },
    [disabled, onClick]
  );

  const Content = useCallback(() => {
    if (href) {
      return (
        <a
          href={href}
          onClick={handleClick}
          className={cn(
            "inline-block",
            commonClasses,
            disabledClasses,
            tooltip && "cursor-pointer",
            className
          )}
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {children}
        </a>
      );
    }

    return (
      <div
        onClick={handleClick}
        className={cn(
          "cursor-pointer children:cursor-pointer",
          commonClasses,
          disabledClasses,
          tooltip && "cursor-pointer",
          className
        )}
        ref={ref as React.Ref<HTMLDivElement>}
        {...rest}
      >
        {children}
      </div>
    );
  }, [
    href,
    handleClick,
    commonClasses,
    disabledClasses,
    className,
    ref,
    rest,
    children,
  ]);

  // If disabled and disabledDescription is provided, wrap with tooltip
  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <Content />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return <Content />;
});

export { Touchable };
